import React, { useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import ListHeader from './list_header';
import EventContainer from '../containers/event_container';
import EventsGroupContainer from '../containers/events_group_container';
import MobilePickerContainer from '../containers/mobile_picker_container';
import SummariesListContainer from '../containers/summaries_list_container';
import SummaryTileContainer from '../containers/summary_tile_container';
import EmptyState from './empty_state';
import useNarrowMobile from '../hooks/useNarrowMobile';
import { splitEventsByFavorites, findEventById } from '../utils/events_utils';
import { VIEW_MODES } from '../constants';
import { componentText } from '../utils/i18n_utils';

const EventsList = ({
  eventsList = {},
  favoritesByEvent = {},
  selectedEventId,
  selectedSummaryKey,
  viewMode,
  isManagerSite,
  isMultiRound,
  showEmptyState = false,
  resultsNotReleased = false,
  isLoadingList = false,
  commitEvents,
  fullClearEventSlice,
  fetchResultScope,
  fetchSummary,
  clearSummaries,
}) => {
  // The mobile/landscape picker sheet (MobilePicker owns its refresh state).
  const pickerRef = useRef(null);
  // Two data columns is all a row carries below NARROW_MOBILE_MAX_WIDTH; the
  // event's Expand View gate has to know (selectShowMobileExpandToggle).
  const narrowMobile = useNarrowMobile();

  // Managers see everything as "other" — no favorites split.
  const { favoriteEvents, otherEvents } = useMemo(
    () =>
      isManagerSite
        ? { favoriteEvents: {}, otherEvents: eventsList }
        : splitEventsByFavorites(eventsList, favoritesByEvent),
    [isManagerSite, eventsList, favoritesByEvent],
  );

  const hasFavorites = Object.keys(favoriteEvents).length > 0;
  const hasOthers = Object.keys(otherEvents).length > 0;

  const handleClickEvent = useCallback((eventId, roundId = null, eventsListArg = null) => {
    clearSummaries();
    if (roundId !== null) commitEvents(roundId, eventsListArg);
    fullClearEventSlice();
    fetchResultScope(eventId, { viewMode });
    pickerRef.current?.close();
  }, [clearSummaries, commitEvents, fullClearEventSlice, fetchResultScope, viewMode]);

  const handleClickSummary = useCallback((summaryKey, roundId = null, eventsListArg = null) => {
    clearSummaries();
    if (roundId !== null) commitEvents(roundId, eventsListArg);
    fullClearEventSlice();
    fetchSummary(summaryKey);
    pickerRef.current?.close();
  }, [clearSummaries, commitEvents, fullClearEventSlice, fetchSummary]);

  // Picking an empty round from the rounds modal: same commit-and-close as
  // picking an event, with nothing to fetch - the board renders the round's
  // empty state (COMMIT_EVENTS stamps emptyEventsList with the list).
  const handleClickEmptyRound = useCallback((roundId, eventsListArg) => {
    clearSummaries();
    commitEvents(roundId, eventsListArg);
    fullClearEventSlice();
    pickerRef.current?.close();
  }, [clearSummaries, commitEvents, fullClearEventSlice]);

  // Opening the picker refreshes the events and round lists (MobilePicker).
  const handleMobileClick = useCallback(() => {
    pickerRef.current?.open();
  }, []);

  if ([VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE].includes(viewMode)) {
    // Single instance shared by the empty-round and selected-tile branches -
    // as two inline copies they silently drifted apart once already.
    const eventsModal = (
      <MobilePickerContainer
        ref={pickerRef}
        viewMode={viewMode}
        handleClickEvent={handleClickEvent}
        handleClickSummary={handleClickSummary}
        handleClickEmptyRound={handleClickEmptyRound}
      />
    );

    // Ahead of the selection check: mobile keeps an event open (fetchLeaderboard
    // reopens one), so a guard behind it would never run on the path that
    // matters - a round change, where the tile on screen is the old round's.
    if (isLoadingList) {
      return (
        <div className={`lbv2__events-list-${viewMode}`}>
          <div className="lbv2__list-loading">
            <div className="lbv2__spinner" role="status" aria-label={componentText('shared.loading', 'Loading')} />
          </div>
        </div>
      )
    }

    if (!selectedEventId && !selectedSummaryKey) {
      if (!showEmptyState) return null;

      // An empty round still needs the rounds modal reachable so the user can
      // move to another round - on multi-round pages the empty state carries a
      // button that opens it.
      return (
        <div className={`lbv2__events-list-${viewMode}`}>
          {eventsModal}
          <EmptyState isMultiRound={isMultiRound} onSelectRound={handleMobileClick} resultsNotReleased={resultsNotReleased} />
        </div>
      );
    }

    // favoritesByEvent is keyed by the parent event id, while selectedEventId
    // is the event-slice key — and can be a snapshot id on multi-round events.
    const selectedEvent = findEventById(eventsList, selectedEventId);
    const isFavorite = Object.prototype.hasOwnProperty.call(favoritesByEvent, selectedEvent?.id);

    return (
      <div className={`lbv2__events-list-${viewMode}`}>
        {eventsModal}
        <ol className={isFavorite ? 'lbv2__with-favorites' : 'lbv2__without-favorites'}>
          {selectedSummaryKey
            ? <SummaryTileContainer
                key={selectedSummaryKey}
                summaryKey={selectedSummaryKey}
                viewMode={viewMode}
                handleMobileClick={handleMobileClick}
              />
            : <EventContainer
                key={selectedEventId}
                id={selectedEventId}
                name={selectedEvent?.name ?? ''}
                viewMode={viewMode}
                narrowMobile={narrowMobile}
                handleMobileClick={handleMobileClick}
              />
          }
        </ol>
      </div>
    );
  }

  return (
    <div className="lbv2__events-list">
      <SummariesListContainer viewMode={viewMode} />
      {isLoadingList && (
        <div className="lbv2__list-loading">
          <div className="lbv2__spinner" role="status" aria-label={componentText('shared.loading', 'Loading')} />
        </div>
      )}
      {!isLoadingList && showEmptyState && (
        <EmptyState isMultiRound={isMultiRound} resultsNotReleased={resultsNotReleased} />
      )}
      {!isLoadingList && hasFavorites && (
        <>
          {hasOthers && <ListHeader>{componentText('events_list.my_leaderboards', 'My Leaderboards')}</ListHeader>}
          <ol className="lbv2__with-favorites">
            {Object.entries(favoriteEvents).map(([specId, events]) => (
              <EventsGroupContainer key={specId} specId={specId} events={events} viewMode={viewMode} />
            ))}
          </ol>
          {hasOthers && <ListHeader>{componentText('events_list.leaderboards', 'Leaderboards')}</ListHeader>}
        </>
      )}

      {!isLoadingList && hasOthers && (
        <ol className="lbv2__without-favorites">
          {Object.entries(otherEvents).map(([specId, events]) => (
            <EventsGroupContainer key={specId} specId={specId} events={events} viewMode={viewMode} />
          ))}
        </ol>
      )}
    </div>
  );
};

EventsList.propTypes = {
  eventsList: PropTypes.object,
  favoritesByEvent: PropTypes.object,
  selectedEventId: PropTypes.string,
  selectedSummaryKey: PropTypes.string,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  isManagerSite: PropTypes.bool,
  isMultiRound: PropTypes.bool,
  showEmptyState: PropTypes.bool,
  resultsNotReleased: PropTypes.bool,
  isLoadingList: PropTypes.bool,
  commitEvents: PropTypes.func,
  fullClearEventSlice: PropTypes.func,
  fetchResultScope: PropTypes.func,
  fetchSummary: PropTypes.func,
  clearSummaries: PropTypes.func,
};

export default EventsList;
